/**
 * Skills route — OpenClaw skills browser.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wrench, X, BookOpen } from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";

type SkillSource = "global" | "openclaw" | "project";

interface SkillInfo {
  name: string;
  path: string;
  realPath: string;
  source: SkillSource;
  description: string | null;
}

interface SkillsResponse {
  skills: SkillInfo[];
  total: number;
}

interface SkillDetailResponse {
  name: string;
  path: string;
  realPath: string;
  source: SkillSource;
  description: string | null;
  content: string;
  error?: string;
}

const SOURCE_LABELS: Record<SkillSource, { label: string; color: string }> = {
  global: { label: "GLOBAL", color: "var(--bw-accent)" },
  openclaw: { label: "OPENCLAW", color: "var(--bw-warn)" },
  project: { label: "PROJECT", color: "var(--bw-success)" },
};

function SkillCard({
  skill,
  onSelect,
}: {
  skill: SkillInfo;
  onSelect: (name: string) => void;
}) {
  return (
    <CorpoCard
      onClick={() => onSelect(skill.name)}
      className="hover:border-[var(--bw-accent)] transition-all"
    >
      <div className="flex items-start gap-3">
        <Wrench size={18} className="text-[var(--bw-accent)] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[var(--bw-fg)]">
              {skill.name}
            </h3>
            <span
              className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 border"
              style={{
                color: SOURCE_LABELS[skill.source].color,
                borderColor: SOURCE_LABELS[skill.source].color,
              }}
            >
              {SOURCE_LABELS[skill.source].label}
            </span>
          </div>
          {skill.description && (
            <p className="text-xs text-[var(--bw-muted)] mt-1 line-clamp-2">
              {skill.description}
            </p>
          )}
        </div>
      </div>
    </CorpoCard>
  );
}

function SkillModal({
  skillName,
  onClose,
}: {
  skillName: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["skill-detail", skillName],
    queryFn: async () => {
      const res = await fetch(`/api/skills/${encodeURIComponent(skillName)}`);
      return res.json() as Promise<SkillDetailResponse>;
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="
          relative z-10 w-full max-w-4xl max-h-[80vh]
          bg-[var(--bw-bg)] border border-[var(--bw-border)]
          flex flex-col
        "
        style={{
          clipPath:
            "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--bw-border)]">
          <div className="flex items-center gap-3">
            <BookOpen size={20} className="text-[var(--bw-accent)]" />
            <CRTHeader text={skillName} level={2} />
          </div>
          <GlyphButton variant="ghost" size="sm" onClick={onClose} icon={<X size={16} />}>
            Cerrar
          </GlyphButton>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="text-[var(--bw-muted)] animate-pulse">
              CARGANDO SKILL...
            </div>
          )}

          {error && (
            <div className="text-[var(--bw-danger)]">
              ⚠ Error cargando skill: {error instanceof Error ? error.message : "Unknown"}
            </div>
          )}

          {data?.error && (
            <div className="text-[var(--bw-danger)]">⚠ {data.error}</div>
          )}

          {data?.content && (
            <pre
              className="
                text-sm font-mono text-[var(--bw-fg)]
                whitespace-pre-wrap break-words
                leading-relaxed
              "
            >
              {data.content}
            </pre>
          )}
        </div>

        {/* Path footer */}
        {data?.path && (
          <div className="p-3 border-t border-[var(--bw-border)] bg-[var(--bw-surface)] flex items-center gap-3">
            <p className="text-xs text-[var(--bw-muted)] font-mono truncate flex-1">
              {data.realPath ?? data.path}
            </p>
            {data.source && (
              <span
                className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 border flex-shrink-0"
                style={{
                  color: SOURCE_LABELS[data.source].color,
                  borderColor: SOURCE_LABELS[data.source].color,
                }}
              >
                {SOURCE_LABELS[data.source].label}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SkillsRoute() {
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["skills"],
    queryFn: async () => {
      const res = await fetch("/api/skills");
      return res.json() as Promise<SkillsResponse>;
    },
  });

  const skills = data?.skills ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CRTHeader text="Skills" level={2} />
          {skills.length > 0 && (
            <span className="text-xs text-[var(--bw-muted)] font-mono">
              [{skills.length}]
            </span>
          )}
        </div>
        <GlyphButton variant="secondary" size="sm" onClick={() => refetch()}>
          Actualizar
        </GlyphButton>
      </div>

      {isLoading && (
        <div className="text-[var(--bw-muted)] animate-pulse">
          ESTABLECIENDO CONEXIÓN...
        </div>
      )}

      {error && (
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)]">
            ⚠ ERROR: {error instanceof Error ? error.message : "Unknown"}
          </p>
        </CorpoCard>
      )}

      {skills.length === 0 && !isLoading && (
        <CorpoCard>
          <div className="text-center py-8">
            <Wrench size={48} className="text-[var(--bw-muted)] mx-auto mb-4" />
            <p className="text-[var(--bw-muted)] uppercase tracking-wider">
              NO HAY SKILLS INSTALADOS
            </p>
          </div>
        </CorpoCard>
      )}

      {skills.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {skills.map((skill) => (
            <SkillCard
              key={skill.name}
              skill={skill}
              onSelect={setSelectedSkill}
            />
          ))}
        </div>
      )}

      {/* Skill detail modal */}
      {selectedSkill && (
        <SkillModal skillName={selectedSkill} onClose={() => setSelectedSkill(null)} />
      )}
    </div>
  );
}
